import { isFloorAt, type TileMapArea, type TileMapContent, type Vector2 } from '@sorrem/shared';

/**
 * The mini map draws the floor the player knows as one shape: every known
 * floor tile, merged, with one outline round its edge. Rooms joined by a
 * doorway read as one plan, and a wall two areas share is never outlined
 * twice.
 *
 * A tile two areas share (a doorway's tiles) shows only once both are
 * known, so a door into a room not yet entered stays part of a straight
 * edge instead of jutting out of it. Wherever floor carries on past the
 * edge (a doorway, a room seen but not entered), that stretch is drawn
 * faded, as an opening.
 */

/** Whether `tile` is walkable floor: ground with no wall on it (the shared `isFloorAt`). */
export function isFloorTile(data: TileMapContent, tile: Vector2): boolean {
    return isFloorAt(data.ground, data.walls, tile);
}

/** Row-major indices (`y * width + x`) of the known floor tiles. */
export function resolveKnownFloor(data: TileMapContent, knownAreaIds: ReadonlySet<string>): Set<number> {
    const { width, height } = data.ground;
    const isFloor = (x: number, y: number): boolean => isFloorTile(data, { x, y });
    /** Per floor tile held by an area: whether all of the areas holding it are known. */
    const knownByTile = new Map<number, { all: boolean }>();
    for (const area of data.areas) {
        const known = knownAreaIds.has(area.id);
        const contains = areaContains(area);
        const minX = Math.max(0, Math.floor(area.x));
        const minY = Math.max(0, Math.floor(area.y));
        const maxX = Math.min(width, Math.ceil(area.x + area.width));
        const maxY = Math.min(height, Math.ceil(area.y + area.height));
        for (let y = minY; y < maxY; y += 1) {
            for (let x = minX; x < maxX; x += 1) {
                if (!isFloor(x, y) || !contains(x + 0.5, y + 0.5)) {
                    continue;
                }
                const index = y * width + x;
                knownByTile.set(index, { all: (knownByTile.get(index)?.all ?? true) && known });
            }
        }
    }
    const tiles = new Set<number>();
    knownByTile.forEach(({ all }, index) => {
        if (all) {
            tiles.add(index);
        }
    });
    return tiles;
}

/** A point test for an area: inside its path when it has one, else its bounding box. */
function areaContains(area: TileMapArea): (x: number, y: number) => boolean {
    const path = area.path;
    if (!path || path.length < 3) {
        return (x, y) => x >= area.x && y >= area.y && x < area.x + area.width && y < area.y + area.height;
    }
    return (x, y) => {
        // Even-odd ray cast along +x; the point is a tile center, never on an edge.
        let inside = false;
        for (let index = 0, previous = path.length - 1; index < path.length; previous = index, index += 1) {
            const a = path[index]!;
            const b = path[previous]!;
            if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) {
                inside = !inside;
            }
        }
        return inside;
    };
}

/** An edge's straight runs, as segments between tile corners: walls, and openings onto more floor. */
export interface TileEdges {
    readonly walls: Vector2[][];
    readonly openings: Vector2[][];
}

/**
 * The edge of `tiles`, split where floor carries on past it (`opensOnto`, an
 * opening) and where it doesn't (a wall), each straight run of one kind a
 * single segment.
 */
export function resolveTileEdges(tiles: ReadonlySet<number>, opensOnto: (x: number, y: number) => boolean, width: number): TileEdges {
    let height = 0;
    tiles.forEach((index) => {
        height = Math.max(height, Math.floor(index / width) + 1);
    });
    const has = (x: number, y: number): boolean => x >= 0 && x < width && y >= 0 && tiles.has(y * width + x);
    /** What the side between a tile and its neighbor is: nothing (both or neither shown), a wall, or an opening. */
    const kindBetween = (a: Vector2, b: Vector2): EdgeKind | null => {
        const inA = has(a.x, a.y);
        if (inA === has(b.x, b.y)) {
            return null;
        }
        const outside = inA ? b : a;
        return opensOnto(outside.x, outside.y) ? 'opening' : 'wall';
    };

    const edges: TileEdges = { walls: [], openings: [] };
    const addRun = (kind: EdgeKind, from: Vector2, to: Vector2): void => {
        (kind === 'wall' ? edges.walls : edges.openings).push([from, to]);
    };
    // Sides along x, on each line between tile rows y - 1 and y.
    for (let y = 0; y <= height; y += 1) {
        let run: { kind: EdgeKind; start: number } | null = null;
        for (let x = 0; x <= width; x += 1) {
            const kind = x < width ? kindBetween({ x, y: y - 1 }, { x, y }) : null;
            if (run && kind !== run.kind) {
                addRun(run.kind, { x: run.start, y }, { x, y });
                run = null;
            }
            if (kind && !run) {
                run = { kind, start: x };
            }
        }
    }
    // Sides along y, on each line between tile columns x - 1 and x.
    for (let x = 0; x <= width; x += 1) {
        let run: { kind: EdgeKind; start: number } | null = null;
        for (let y = 0; y <= height; y += 1) {
            const kind = y < height ? kindBetween({ x: x - 1, y }, { x, y }) : null;
            if (run && kind !== run.kind) {
                addRun(run.kind, { x, y: run.start }, { x, y });
                run = null;
            }
            if (kind && !run) {
                run = { kind, start: y };
            }
        }
    }
    return edges;
}

type EdgeKind = 'wall' | 'opening';

interface OutlineEdge {
    readonly from: Vector2;
    readonly to: Vector2;
    readonly direction: Vector2;
}

/**
 * The edge of a set of tiles as closed loops of tile corners, each straight
 * run one segment. Loops run with the tiles on their right (y down), so a
 * hole inside the shape (a pillar) winds the other way.
 */
export function resolveTileOutline(tiles: ReadonlySet<number>, width: number): Vector2[][] {
    const has = (x: number, y: number): boolean => x >= 0 && x < width && y >= 0 && tiles.has(y * width + x);
    const key = (point: Vector2): string => `${point.x},${point.y}`;
    const edgesFrom = new Map<string, OutlineEdge[]>();
    const addEdge = (from: Vector2, to: Vector2): void => {
        const edge = { from, to, direction: { x: Math.sign(to.x - from.x), y: Math.sign(to.y - from.y) } };
        const list = edgesFrom.get(key(from)) ?? [];
        list.push(edge);
        edgesFrom.set(key(from), list);
    };
    for (const index of tiles) {
        const x = index % width;
        const y = Math.floor(index / width);
        if (!has(x, y - 1)) addEdge({ x, y }, { x: x + 1, y });
        if (!has(x + 1, y)) addEdge({ x: x + 1, y }, { x: x + 1, y: y + 1 });
        if (!has(x, y + 1)) addEdge({ x: x + 1, y: y + 1 }, { x, y: y + 1 });
        if (!has(x - 1, y)) addEdge({ x, y: y + 1 }, { x, y });
    }

    const used = new Set<OutlineEdge>();
    const loops: Vector2[][] = [];
    for (const start of [...edgesFrom.values()].flat()) {
        if (used.has(start)) {
            continue;
        }
        const loop: Vector2[] = [];
        let edge: OutlineEdge | undefined = start;
        while (edge && !used.has(edge)) {
            used.add(edge);
            loop.push(edge.from);
            edge = nextEdge(edge, (edgesFrom.get(key(edge.to)) ?? []).filter((candidate) => !used.has(candidate)));
        }
        // Keep a corner only where the outline turns.
        loops.push(dropStraightCorners(loop));
    }
    return loops;
}

/** Of the edges leaving a corner, the one that follows `edge`: where two tiles touch only at that corner, the one turning toward the tiles (right). */
function nextEdge(edge: OutlineEdge, outgoing: readonly OutlineEdge[]): OutlineEdge | undefined {
    if (outgoing.length <= 1) {
        return outgoing[0];
    }
    const right = { x: -edge.direction.y, y: edge.direction.x };
    return outgoing.find((candidate) => candidate.direction.x * right.x + candidate.direction.y * right.y > 0) ?? outgoing[0];
}

function continuesStraight(a: Vector2, b: Vector2, c: Vector2): boolean {
    return (b.x - a.x) * (c.y - b.y) === (b.y - a.y) * (c.x - b.x);
}

/** Removes corners a loop passes straight through, including where it closes. */
function dropStraightCorners(loop: readonly Vector2[]): Vector2[] {
    return loop.filter((point, index) => {
        const before = loop[(index + loop.length - 1) % loop.length]!;
        const after = loop[(index + 1) % loop.length]!;
        return !continuesStraight(before, point, after);
    });
}
